import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'

interface DeviceAlert {
  id: string
  device_id: string
  device_name: string
  device_type: string
  alert_type: 'error' | 'warning' | 'info' | 'critical' | 'maintenance'
  severity: 'low' | 'medium' | 'high' | 'critical'
  status: 'active' | 'acknowledged' | 'resolved' | 'suppressed'
  title: string
  message: string
  description: string
  source: 'system' | 'sensor' | 'user' | 'scheduled' | 'threshold'
  trigger_condition: string
  current_value?: number
  threshold_value?: number
  unit?: string
  location: string
  department: string
  created_at: string
  acknowledged_at?: string
  acknowledged_by?: string
  resolved_at?: string
  resolved_by?: string
  escalation_level: number
  escalated_at?: string
  notification_sent: boolean
  auto_resolve: boolean
  resolution_notes?: string
  related_alerts: string[]
}

interface AlertRule {
  id: string
  name: string
  description: string
  device_type: string
  parameter: string
  condition: 'greater_than' | 'less_than' | 'equals' | 'not_equals' | 'between' | 'outside_range'
  threshold_value: number
  threshold_value_2?: number
  unit: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  is_active: boolean
  auto_acknowledge: boolean
  auto_resolve: boolean
  notification_enabled: boolean
  escalation_enabled: boolean
  escalation_delay: number
  created_by: string
  created_at: string
}

interface AlertSummary {
  total_alerts: number
  active_alerts: number
  critical_alerts: number
  unacknowledged_alerts: number
  alerts_today: number
  top_device: string
  most_common_type: string
}

const DeviceAlerts: React.FC = () => {
  const [alerts, setAlerts] = useState<DeviceAlert[]>([])
  const [rules, setRules] = useState<AlertRule[]>([])
  const [summary, setSummary] = useState<AlertSummary | null>(null)
  const [selectedAlert, setSelectedAlert] = useState<DeviceAlert | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [severityFilter, setSeverityFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [activeTab, setActiveTab] = useState<'alerts' | 'rules' | 'analytics'>('alerts')
  const [showCreateRuleModal, setShowCreateRuleModal] = useState(false)
  const [showAlertModal, setShowAlertModal] = useState(false)

  const [ruleFormData, setRuleFormData] = useState({
    name: '',
    description: '',
    device_type: '',
    parameter: '',
    condition: 'greater_than' as const,
    threshold_value: 0,
    threshold_value_2: 0,
    unit: '',
    severity: 'medium' as const,
    is_active: true,
    auto_acknowledge: false,
    auto_resolve: false,
    notification_enabled: true,
    escalation_enabled: false,
    escalation_delay: 60
  })

  const fetchAlerts = async () => {
    try {
      setIsLoading(true)
      const params = new URLSearchParams({
        search: searchTerm,
        severity: severityFilter,
        status: statusFilter,
        type: typeFilter
      })

      const response = await fetch(`/api/v1/devices/alerts/?${params}`)
      if (response.ok) {
        const data = await response.json()
        setAlerts(data.results || data)
      } else {
        toast.error('Failed to fetch alerts')
      }
    } catch (error) {
      console.error('Error fetching alerts:', error)
      toast.error('Error fetching alerts')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchRules = async () => {
    try {
      const response = await fetch('/api/v1/devices/alert-rules/')
      if (response.ok) {
        const data = await response.json()
        setRules(data.results || data)
      }
    } catch (error) {
      console.error('Error fetching rules:', error)
    }
  }

  const fetchSummary = async () => {
    try {
      const response = await fetch('/api/v1/devices/alerts/summary/')
      if (response.ok) {
        const data = await response.json()
        setSummary(data)
      }
    } catch (error) {
      console.error('Error fetching summary:', error)
    }
  }

  useEffect(() => {
    fetchAlerts()
    fetchRules()
    fetchSummary()

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchAlerts()
      fetchSummary()
    }, 30000)

    return () => clearInterval(interval)
  }, [searchTerm, severityFilter, statusFilter, typeFilter])

  const acknowledgeAlert = async (alertId: string) => {
    try {
      const response = await fetch(`/api/v1/devices/alerts/${alertId}/acknowledge/`, {
        method: 'POST'
      })

      if (response.ok) {
        toast.success('Alert acknowledged')
        fetchAlerts()
        fetchSummary()
      } else {
        toast.error('Failed to acknowledge alert')
      }
    } catch (error) {
      console.error('Error acknowledging alert:', error)
      toast.error('Error acknowledging alert')
    }
  }

  const resolveAlert = async (alertId: string, notes: string) => {
    try {
      const response = await fetch(`/api/v1/devices/alerts/${alertId}/resolve/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ resolution_notes: notes })
      })

      if (response.ok) {
        toast.success('Alert resolved')
        fetchAlerts()
        fetchSummary()
      } else {
        toast.error('Failed to resolve alert')
      }
    } catch (error) {
      console.error('Error resolving alert:', error)
      toast.error('Error resolving alert')
    }
  }


  const createAlertRule = async () => {
    try {
      const response = await fetch('/api/v1/devices/alert-rules/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(ruleFormData)
      })

      if (response.ok) {
        toast.success('Alert rule created successfully')
        setShowCreateRuleModal(false)
        fetchRules()
      } else {
        toast.error('Failed to create alert rule')
      }
    } catch (error) {
      console.error('Error creating rule:', error)
      toast.error('Error creating rule')
    }
  }

  const toggleRule = async (ruleId: string, isActive: boolean) => {
    try {
      const response = await fetch(`/api/v1/devices/alert-rules/${ruleId}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !isActive })
      })

      if (response.ok) {
        toast.success(`Rule ${!isActive ? 'enabled' : 'disabled'}`)
        fetchRules()
      } else {
        toast.error('Failed to update rule')
      }
    } catch (error) {
      console.error('Error updating rule:', error)
      toast.error('Error updating rule')
    }
  }

  const getSeverityColor = (severity: string) => {
    const colors: Record<string, string> = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      critical: 'bg-red-100 text-red-800'
    }
    return colors[severity] || 'bg-gray-100 text-gray-800'
  }

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      active: 'bg-red-100 text-red-800',
      acknowledged: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-green-100 text-green-800',
      suppressed: 'bg-gray-100 text-gray-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      error: 'bg-red-100 text-red-800',
      warning: 'bg-yellow-100 text-yellow-800',
      info: 'bg-blue-100 text-blue-800',
      critical: 'bg-red-200 text-red-900',
      maintenance: 'bg-purple-100 text-purple-800'
    }
    return colors[type] || 'bg-gray-100 text-gray-800'
  }

  const getSeverityIcon = (severity: string) => {
    const icons: Record<string, string> = {
      low: '🟢',
      medium: '🟡',
      high: '🟠',
      critical: '🔴'
    }
    return icons[severity] || '⚫'
  }

  const criticalAlerts = alerts.filter(a => a.severity === 'critical' && a.status === 'active')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Device Alerts</h1>
          <p className="text-gray-600">Monitor and manage device alerts and notifications</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowCreateRuleModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            New Rule
          </button>
          <button className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700">
            Export Report
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {[
            { label: 'Total Alerts', value: summary.total_alerts, color: 'blue', icon: '📊' },
            { label: 'Active', value: summary.active_alerts, color: 'red', icon: '🚨' },
            { label: 'Critical', value: summary.critical_alerts, color: 'red', icon: '🔴' },
            { label: 'Unacknowledged', value: summary.unacknowledged_alerts, color: 'yellow', icon: '⚠️' },
            { label: 'Today', value: summary.alerts_today, color: 'purple', icon: '📅' },
            { label: 'Top Device', value: summary.top_device, color: 'green', icon: '📱', isText: true },
            { label: 'Common Type', value: summary.most_common_type, color: 'orange', icon: '🏷️', isText: true }
          ].map((stat) => (
            <div key={stat.label} className="bg-white p-4 rounded-lg border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-600">{stat.label}</p>
                  <p className={`text-lg font-bold text-gray-900 ${stat.isText ? 'text-sm' : ''}`}>
                    {stat.isText ? (stat.value || 'N/A') : stat.value}
                  </p>
                </div>
                <div className="text-lg">{stat.icon}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Critical Alerts Banner */}
      {criticalAlerts.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center mb-3">
            <span className="text-2xl mr-2">🚨</span>
            <h3 className="text-lg font-semibold text-red-900">
              {criticalAlerts.length} Critical Alert{criticalAlerts.length > 1 ? 's' : ''} Require Immediate Attention
            </h3>
          </div>
          <div className="space-y-2">
            {criticalAlerts.slice(0, 3).map((alert) => (
              <div key={alert.id} className="flex justify-between items-center p-3 bg-white rounded border border-red-200">
                <div>
                  <p className="font-medium text-red-900">{alert.device_name}</p>
                  <p className="text-sm text-red-700">{alert.title}</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => acknowledgeAlert(alert.id)}
                    className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                  >
                    Acknowledge
                  </button>
                  <button
                    onClick={() => {
                      setSelectedAlert(alert)
                      setShowAlertModal(true)
                    }}
                    className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700"
                  >
                    Details
                  </button>
                </div>
              </div>
            ))}
            {criticalAlerts.length > 3 && (
              <p className="text-sm text-red-600">
                And {criticalAlerts.length - 3} more critical alert{criticalAlerts.length - 3 > 1 ? 's' : ''}...
              </p>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { key: 'alerts', label: `Alerts (${alerts.length})` },
            { key: 'rules', label: `Rules (${rules.length})` },
            { key: 'analytics', label: 'Analytics' }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Alerts Tab */}
      {activeTab === 'alerts' && (
        <>
          {/* Filters */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <input
                  type="text"
                  placeholder="Search alerts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Severity</label>
                <select
                  value={severityFilter}
                  onChange={(e) => setSeverityFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Severity</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="acknowledged">Acknowledged</option>
                  <option value="resolved">Resolved</option>
                  <option value="suppressed">Suppressed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Types</option>
                  <option value="error">Error</option>
                  <option value="warning">Warning</option>
                  <option value="info">Info</option>
                  <option value="critical">Critical</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </div>
            </div>
          </div>

          {/* Alerts Table */}
          <div className="bg-white shadow-sm rounded-lg border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Alert / Device
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type / Severity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Value
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {isLoading ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                        Loading alerts...
                      </td>
                    </tr>
                  ) : alerts.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                        No alerts found.
                      </td>
                    </tr>
                  ) : (
                    alerts.map((alert) => (
                      <tr key={alert.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className="text-lg mr-2">{getSeverityIcon(alert.severity)}</span>
                            <div>
                              <div className="text-sm font-medium text-gray-900">{alert.title}</div>
                              <div className="text-sm text-gray-500">{alert.device_name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="space-y-1">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(alert.alert_type)}`}>
                              {alert.alert_type.toUpperCase()}
                            </span>
                            <br />
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSeverityColor(alert.severity)}`}>
                              {alert.severity.toUpperCase()}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(alert.status)}`}>
                            {alert.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {alert.current_value && alert.threshold_value ? (
                            <div>
                              <div>Current: {alert.current_value} {alert.unit}</div>
                              <div className="text-gray-500">Threshold: {alert.threshold_value} {alert.unit}</div>
                            </div>
                          ) : (
                            'N/A'
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(alert.created_at).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => {
                                setSelectedAlert(alert)
                                setShowAlertModal(true)
                              }}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              View
                            </button>
                            {alert.status === 'active' && (
                              <button
                                onClick={() => acknowledgeAlert(alert.id)}
                                className="text-yellow-600 hover:text-yellow-900"
                              >
                                Acknowledge
                              </button>
                            )}
                            {(alert.status === 'active' || alert.status === 'acknowledged') && (
                              <button
                                onClick={() => resolveAlert(alert.id, 'Resolved from alerts page')}
                                className="text-green-600 hover:text-green-900"
                              >
                                Resolve
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Rules Tab */}
      {activeTab === 'rules' && (
        <div className="space-y-4">
          {rules.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border">
              <p className="text-gray-500">No alert rules configured</p>
            </div>
          ) : (
            rules.map((rule) => (
              <div key={rule.id} className="bg-white rounded-lg border p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{rule.name}</h3>
                    <p className="text-sm text-gray-600">{rule.description}</p>
                    <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                      <span>Device Type: {rule.device_type}</span>
                      <span>Parameter: {rule.parameter}</span>
                      <span>Condition: {rule.condition.replace('_', ' ')}</span>
                      <span>Threshold: {rule.threshold_value} {rule.unit}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getSeverityColor(rule.severity)}`}>
                      {rule.severity.toUpperCase()}
                    </span>
                    <button
                      onClick={() => toggleRule(rule.id, rule.is_active)}
                      className={`px-3 py-1 text-xs rounded ${
                        rule.is_active
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-red-100 text-red-800 hover:bg-red-200'
                      }`}
                    >
                      {rule.is_active ? 'Enabled' : 'Disabled'}
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Create Rule Modal */}
      {showCreateRuleModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Create Alert Rule</h3>
              <button
                onClick={() => setShowCreateRuleModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rule Name</label>
                <input
                  type="text"
                  value={ruleFormData.name}
                  onChange={(e) => setRuleFormData({...ruleFormData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Device Type</label>
                <input
                  type="text"
                  value={ruleFormData.device_type}
                  onChange={(e) => setRuleFormData({...ruleFormData, device_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Parameter</label>
                <input
                  type="text"
                  value={ruleFormData.parameter}
                  onChange={(e) => setRuleFormData({...ruleFormData, parameter: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Condition</label>
                <select
                  value={ruleFormData.condition}
                  onChange={(e) => setRuleFormData({...ruleFormData, condition: e.target.value as any})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="greater_than">Greater Than</option>
                  <option value="less_than">Less Than</option>
                  <option value="equals">Equals</option>
                  <option value="not_equals">Not Equals</option>
                  <option value="between">Between</option>
                  <option value="outside_range">Outside Range</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Threshold Value</label>
                <input
                  type="number"
                  value={ruleFormData.threshold_value}
                  onChange={(e) => setRuleFormData({...ruleFormData, threshold_value: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Unit</label>
                <input
                  type="text"
                  value={ruleFormData.unit}
                  onChange={(e) => setRuleFormData({...ruleFormData, unit: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Severity</label>
                <select
                  value={ruleFormData.severity}
                  onChange={(e) => setRuleFormData({...ruleFormData, severity: e.target.value as any})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Escalation Delay (minutes)</label>
                <input
                  type="number"
                  value={ruleFormData.escalation_delay}
                  onChange={(e) => setRuleFormData({...ruleFormData, escalation_delay: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  min="1"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={ruleFormData.description}
                  onChange={(e) => setRuleFormData({...ruleFormData, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="mt-4 space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={ruleFormData.is_active}
                  onChange={(e) => setRuleFormData({...ruleFormData, is_active: e.target.checked})}
                  className="mr-2"
                />
                Rule Active
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={ruleFormData.notification_enabled}
                  onChange={(e) => setRuleFormData({...ruleFormData, notification_enabled: e.target.checked})}
                  className="mr-2"
                />
                Enable Notifications
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={ruleFormData.escalation_enabled}
                  onChange={(e) => setRuleFormData({...ruleFormData, escalation_enabled: e.target.checked})}
                  className="mr-2"
                />
                Enable Escalation
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={ruleFormData.auto_resolve}
                  onChange={(e) => setRuleFormData({...ruleFormData, auto_resolve: e.target.checked})}
                  className="mr-2"
                />
                Auto Resolve
              </label>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => setShowCreateRuleModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={createAlertRule}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
              >
                Create Rule
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Detail Modal */}
      {showAlertModal && selectedAlert && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-3xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                Alert Details - {selectedAlert.device_name}
              </h3>
              <button
                onClick={() => setShowAlertModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Alert Information</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Title:</span> {selectedAlert.title}</p>
                    <p><span className="font-medium">Device:</span> {selectedAlert.device_name}</p>
                    <p><span className="font-medium">Type:</span> {selectedAlert.alert_type}</p>
                    <p><span className="font-medium">Severity:</span>
                      <span className={`ml-2 px-2 py-1 text-xs rounded-full ${getSeverityColor(selectedAlert.severity)}`}>
                        {selectedAlert.severity.toUpperCase()}
                      </span>
                    </p>
                    <p><span className="font-medium">Source:</span> {selectedAlert.source}</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Values</h4>
                  <div className="space-y-2 text-sm">
                    {selectedAlert.current_value && (
                      <p><span className="font-medium">Current Value:</span> {selectedAlert.current_value} {selectedAlert.unit}</p>
                    )}
                    {selectedAlert.threshold_value && (
                      <p><span className="font-medium">Threshold:</span> {selectedAlert.threshold_value} {selectedAlert.unit}</p>
                    )}
                    <p><span className="font-medium">Trigger:</span> {selectedAlert.trigger_condition}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Timeline</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Created:</span> {new Date(selectedAlert.created_at).toLocaleString()}</p>
                    {selectedAlert.acknowledged_at && (
                      <p><span className="font-medium">Acknowledged:</span> {new Date(selectedAlert.acknowledged_at).toLocaleString()}</p>
                    )}
                    {selectedAlert.resolved_at && (
                      <p><span className="font-medium">Resolved:</span> {new Date(selectedAlert.resolved_at).toLocaleString()}</p>
                    )}
                    <p><span className="font-medium">Escalation Level:</span> {selectedAlert.escalation_level}</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Location</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Location:</span> {selectedAlert.location}</p>
                    <p><span className="font-medium">Department:</span> {selectedAlert.department}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <h4 className="font-semibold text-gray-900 mb-2">Message</h4>
              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                {selectedAlert.message}
              </p>
            </div>

            <div className="mt-6">
              <h4 className="font-semibold text-gray-900 mb-2">Description</h4>
              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                {selectedAlert.description}
              </p>
            </div>

            {selectedAlert.resolution_notes && (
              <div className="mt-6">
                <h4 className="font-semibold text-gray-900 mb-2">Resolution Notes</h4>
                <p className="text-sm text-gray-600 bg-green-50 p-3 rounded">
                  {selectedAlert.resolution_notes}
                </p>
              </div>
            )}

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => setShowAlertModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
              {selectedAlert.status === 'active' && (
                <>
                  <button
                    onClick={() => {
                      acknowledgeAlert(selectedAlert.id)
                      setShowAlertModal(false)
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 border border-transparent rounded-md hover:bg-yellow-700"
                  >
                    Acknowledge
                  </button>
                  <button
                    onClick={() => {
                      resolveAlert(selectedAlert.id, 'Resolved from alert details')
                      setShowAlertModal(false)
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
                  >
                    Resolve
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DeviceAlerts